const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://us-central1-datos-democracia.cloudfunctions.net/api"
    : "http://localhost:5001/datos-democracia/us-central1/api");

/**
 * Cola global de peticiones al Banco Central.
 *
 * Cada gráfico pide su serie al montarse, así que una sección como Economía
 * dispara ~10 POST simultáneos y el BC responde 502 de forma intermitente.
 * Encadenando las peticiones salen de a una, y como React monta los
 * componentes en el orden del documento, los gráficos se llenan de arriba
 * hacia abajo.
 */
let cola = Promise.resolve();

function enCola(tarea) {
  const resultado = cola.then(tarea, tarea);
  cola = resultado.catch(() => {}); // un fallo no debe cortar la fila
  return resultado;
}

/**
 * Caché de series por sesión.
 *
 * Cada gráfico pide su serie al montarse, así que sin esto volver a Economía
 * (o cambiar de pestaña y regresar) redispara todas las peticiones. Se guarda
 * la promesa, no el resultado: si dos gráficos piden la misma serie a la vez,
 * comparten una sola petición en lugar de encolar dos.
 *
 * Las series del BC se actualizan como mucho una vez al día, así que la caché
 * dura lo que dure la pestaña abierta.
 */
const cache = new Map();

export async function fetchSerie({ serieId, startDate = "2009-01-01", endDate }) {
  const end = endDate || new Date().toISOString().slice(0, 10);
  const clave = `${serieId}|${startDate}|${end}`;

  if (!cache.has(clave)) {
    const pedido = enCola(async () => {
      const res = await fetch(`${API_BASE}/banco-central/serie`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serieId, startDate, endDate: end }),
      });
      if (!res.ok) throw new Error(`BC error ${res.status}`);
      const json = await res.json();
      const obs = json?.Series?.Obs || [];
      return obs.map((o) => ({
        fecha: o.indexDateString,
        valor: parseFloat(o.value),
      })).filter((o) => !isNaN(o.valor));
    });
    // Un fallo no se cachea: así el próximo intento vuelve a pedirla.
    pedido.catch(() => cache.delete(clave));
    cache.set(clave, pedido);
  }

  return cache.get(clave);
}

export const SERIES_BC = {
  PIB_REAL:        "F032.PIB.FLU.R.CLP.2018.Z.Z.1.M",
  IMACEC_DESEST:   "F032.IMC.IND.Z.Z.EP18.Z.Z.1.M",
  IMACEC_EMPALM:   "F032.IMC.IND.Z.Z.EP18.Z.Z.0.M",
  IPC_VAR_ANUAL:   "F074.IPC.V12.Z.EP23.C.M",
  IPC_SIN_VOL:     "G073.IPCSV.V12.2023.M",
  DEUDA_BRUTA:     "F051.D7.PPB.C.Z.Z.T",
  EXPORTACIONES:   "F068.B1.FLU.Z.0.C.N.Z.Z.Z.Z.6.3.T",
  IMPORTACIONES:   "F068.B1.FLU.Z.0.M.N.0.Z.Z.Z.6.3.T",
  TPM_MENSUAL:     "F022.TPM.TIN.M001.NO.Z.M",
  TIPO_CAMBIO:     "F073.TCO.PRE.Z.D",
  // Precio del cobre refinado BML (dólares/libra)
  PRECIO_COBRE:    "F019.PPB.PRE.40.M",
  // FBCF y consumo: volumen a precios del año anterior encadenado, ref. 2018, series empalmadas (trimestral)
  FBCF:            "F033.FKF.FLU.R.CLP.EP18.0.T",
  CONSUMO_HOGARES: "F033.CPR.FLU.R.CLP.EP18.0.T",
};

/**
 * Desde cuándo se pide cada serie. Vive acá y no en los props de cada gráfico
 * para que el precargado use exactamente la misma clave de caché: si la fecha
 * estuviera duplicada en el JSX, cambiarla ahí dejaría el precargado apuntando
 * a una clave distinta y volvería a pedir todo al entrar a la sección.
 */
export const INICIO_SERIE = {
  [SERIES_BC.IPC_VAR_ANUAL]:   "2015-01-01",
  [SERIES_BC.IPC_SIN_VOL]:     "2015-01-01",
  [SERIES_BC.IMACEC_DESEST]:   "2013-01-01",
  [SERIES_BC.IMACEC_EMPALM]:   "2013-01-01",
  [SERIES_BC.PIB_REAL]:        "2012-01-01",
  [SERIES_BC.FBCF]:            "2009-01-01",
  [SERIES_BC.CONSUMO_HOGARES]: "2009-01-01",
  [SERIES_BC.TIPO_CAMBIO]:     "2010-01-01",
  [SERIES_BC.PRECIO_COBRE]:    "2010-01-01",
  [SERIES_BC.DEUDA_BRUTA]:     "2006-01-01",
};

/**
 * Pide todas las series de Economía al arrancar la app. Van por la misma cola
 * (de a una) y quedan en caché, así que entrar a Economía ya las encuentra
 * listas en vez de dispararlas de nuevo. Los errores se ignoran: si alguna
 * falla, el gráfico correspondiente la volverá a pedir y mostrará su error.
 */
export function precargarSeries() {
  for (const [serieId, startDate] of Object.entries(INICIO_SERIE)) {
    fetchSerie({ serieId, startDate }).catch(() => {});
  }
}

/** Variación % respecto al mismo período del año anterior (series con fecha dd-mm-aaaa) */
export function variacionAnual(serie) {
  const porClave = new Map(
    serie.map((s) => {
      const [dd, mm, yyyy] = s.fecha.split("-");
      return [`${dd}-${mm}-${yyyy}`, s.valor];
    })
  );
  return serie
    .map((s) => {
      const [dd, mm, yyyy] = s.fecha.split("-");
      const previa = porClave.get(`${dd}-${mm}-${Number(yyyy) - 1}`);
      if (previa == null || previa === 0) return null;
      return { fecha: s.fecha, valor: ((s.valor / previa) - 1) * 100 };
    })
    .filter(Boolean);
}
